import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";

import prepare_headers from "../../../store/prepare_headers";

export interface IRegionAutocompleteItem {
    id: number;
    slug: string;
    name: string;
    short_name?: string | null;
    full_name?: string | null;
    full_name_reverted?: string | null;
    type: number;
    parent_full_name?: string | null;
    has_charts_access: boolean;
}

export interface IRegionGeoArea {
    type: "Polygon" | "MultiPolygon" | string;
    coordinates: [number, number][][] | [number, number][][][];
}

export interface IRegionCenter {
    lat: number;
    lng: number;
}

export interface IRegionGeometry {
    id: number;
    slug: string;
    name?: string | null;
    full_name?: string | null;
    type: number;
    geo_area?: IRegionGeoArea | null;
    center?: IRegionCenter | null;
    /**
     * `false` for voivodeship (`type=1`) — no simple per-region series in DB (month-stats aggregate by powiat
     * instead). Polygon still renders; FE should steer selection toward city/district for stats-bearing views.
     */
    stats_supported: boolean;
}

interface RegionAutocompleteParams {
    q?: string;
    /** Omit `q` and pass `permitted: 1` to get the user's `UserRegionPermission` regions (same response shape) — default list for the city select. */
    permitted?: 1;
    types?: string;
    limit?: number;
}

interface RegionsGeometryByParentParams {
    parentId: number;
    /** Child region type filter (e.g. `6` for districts) — same semantics as legacy `region-list-map&type=`. */
    type?: number;
}

export const chartsRegionsApi = createApi({
    reducerPath: "chartsRegionsApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `/api/regions/`,
        prepareHeaders: prepare_headers,
        credentials: "include"
    }),
    endpoints: (builder) => ({
        getRegionAutocomplete: builder.query<IRegionAutocompleteItem[], RegionAutocompleteParams>({
            query: ({q, permitted, types, limit}) => ({
                url: "autocomplete/",
                params: {
                    ...(q ? {q} : {}),
                    ...(permitted ? {permitted} : {}),
                    ...(types ? {types} : {}),
                    ...(limit != null ? {limit} : {})
                }
            })
        }),
        getRegionGeometry: builder.query<IRegionGeometry, number>({
            query: (regionId) => `${regionId}/geometry/`
        }),
        /** Children of a region (e.g. districts of a city). Proxy to RP API: `children_regions` + `region-list-map`. */
        getRegionsGeometryByParent: builder.query<IRegionGeometry[], RegionsGeometryByParentParams>({
            query: ({parentId, type}) => ({
                url: "geometry/",
                params: {parent_id: parentId, ...(type != null ? {type} : {})}
            })
        })
    })
});

export const {useGetRegionAutocompleteQuery, useGetRegionGeometryQuery, useGetRegionsGeometryByParentQuery} =
    chartsRegionsApi;
